import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';

import AuthSettingsStore from '../../AuthSettingsStore';
import UserStore from '../../UserStore';
import { OpenOptions } from '@/config/openOptions';
import { EntityLoadErrorType, LinkDetails, RecipientPhoneInfo } from '@/types/types';
import { ALLOW_FORWARDING, CURRENT_ORGANIZATION } from '@/consts';
import {
    AddPermissionsArgs,
    SharedFile,
    PermissionsErrors,
    PermissionsState,
    PermissionsSummary,
} from './interfaces';
import { getFile } from '../../../api';
import {
    captureErrorForSentry,
    DeferredTask,
    eqUserIdentities,
    getPolicyAggregate,
    PolicyAggregate,
    RequestsQueue,
} from '../../../components/utils';
import {
    captureLinkLoadError,
    checkIsPermissionsError,
    errorToErrorType,
    fetchExistingLink,
    fetchFilesChunked,
    getAllFilesPoliciesAggregate,
    getDebugInfo,
    tryAddPermissionsToFile,
} from './helpers';
import { UploadProcess } from '@/stores/UploadFilesStore/interfaces';
import appConfig from '@/config/env';

export type {
    EntityLoadErrorType,
    PermissionsSummary,
    PermissionsErrors,
    SharedFile,
};
export { getDebugInfo };

const ADD_PERMISSIONS_DEFAULT_ARGS: Required<AddPermissionsArgs> = {
    isSilent: false,
    targetFilesIds: [],
};

class SharedFilesStore {
    constructor(authSettingsStore: AuthSettingsStore, userStore: UserStore) {
        this.authSettingsStore = authSettingsStore;
        this.userStore = userStore;
        this.permissionsCheckQueue = new RequestsQueue<DeferredTask>();
        makeObservable(this);
    }

    private readonly authSettingsStore: AuthSettingsStore;

    private readonly userStore: UserStore;

    private readonly permissionsCheckQueue: RequestsQueue<DeferredTask>;

    @observable
    isLoading = true;

    @observable
    isContentLoading = true;

    @observable
    errorType: EntityLoadErrorType = null;

    @observable
    linkData: LinkDetails = null;

    @observable
    isMFARequired: boolean = false;

    @observable
    linkId: string;

    @observable
    filesList: SharedFile[] = [];

    @observable
    selectedFileId: string;

    @observable
    selectedFileIndex: number;

    @observable
    filesPermissions: Map<string, PermissionsState> = new Map<string, PermissionsState>();

    @observable
    linkErrorObject: unknown;

    @observable
    allRequestsMap: Map<string, boolean> = new Map<string, boolean>();

    @observable
    permissionsLoaded: boolean = false;

    @observable
    passwordHint: string | null = appConfig.PASSWORD_DEFAULT_HINT || null;

    @computed
    get anyRequestInProcess(): boolean {
        return [...this.allRequestsMap.values()].some((i) => i === true);
    }

    @computed
    get anyPermissionFail(): boolean {
        return [...this.filesPermissions.values()].some((i) => i.summary === 'noPermissions');
    }

    @computed
    get allPermissionsFail(): boolean {
        const entries = [...this.filesPermissions.values()];
        return entries.length > 0 && entries.every((i) => i.summary === 'noPermissions');
    }

    @computed
    get accessibleFiles(): SharedFile[] {
        return this.filesList.filter(({ file_id }) => {
            const permission = this.filesPermissions.get(file_id);
            return permission?.summary === 'permissionsAdded';
        });
    }

    @computed
    get accessibleFilesAllowForwarding(): boolean {
        return this.accessibleFiles.some(({ policy }) => {
            const accessSettings = policy?.settings?.access ?? [];
            return accessSettings.some((s) => s?.id === ALLOW_FORWARDING && !!s.value);
        });
    }

    @computed
    get allFilesPoliciesAggregate(): PolicyAggregate {
        return getAllFilesPoliciesAggregate(this.filesList);
    }

    @computed
    get isMainDataLoaded(): boolean {
        return !!this.filesList.length || !!this.errorType;
    }

    @computed
    get selectedFilePolicyAggregate(): PolicyAggregate {
        const { selectedFile } = this;
        return selectedFile ? getPolicyAggregate(selectedFile.policy) : null;
    }

    @computed
    get recipientEmail(): string {
        return this.linkData?.recp_email;
    }

    @computed
    get senderEmail(): string {
        return this.linkData?.src_email;
    }

    @computed
    get selectedFile(): SharedFile {
        return this.filesList.find(({ file_id: fileId }) => fileId === this.selectedFileId);
    }

    @computed
    get itemsIds(): string[] {
        return this.filesList.map<string>(({ file_id: fileId }) => fileId);
    }

    @computed
    get fileItemsIds(): string[] {
        return this.filesList.reduce<string[]>((acc, current) => {
            if (!current.is_folder) {
                acc.push(current.file_id);
            }
            return acc;
        }, []);
    }

    @computed
    get filesWithDownloadOptionIds(): string[] {
        return this.accessibleFiles.reduce<string[]>((acc, current) => {
            if (
                current.is_folder
                || current.is_workspace
                || current.allowed_apps?.includes(OpenOptions.download)
            ) {
                acc.push(current.file_id);
            }
            return acc;
        }, []);
    }

    @computed
    get downloadableFilesIds(): string[] {
        return this.filesWithDownloadOptionIds.filter((fileId) => (
            !checkIsPermissionsError(this.filesPermissions.get(fileId)?.summary)
        ));
    }

    @computed
    get allFilesCount(): number {
        return this.filesList.length;
    }

    @computed
    get attachmentsCount(): number {
        const messageId = this.linkData?.message_id;
        if (!messageId) {
            return this.accessibleFiles.length;
        }
        return this.accessibleFiles.filter(({ file_id }) => file_id !== messageId).length;
    }

    @computed
    get selectedFilePermissions(): PermissionsState {
        return this.filesPermissions.get(this.selectedFileId);
    }

    @computed
    get selectedFilePermissionsSummary(): PermissionsSummary {
        return this.selectedFilePermissions?.summary;
    }

    @computed
    get baseDebugInfo(): Record<string, string> {
        return {
            linkId: this.linkId,
            userId: this.userStore.currentUserId,
            email: this.userStore.currentUserEmail,
        };
    }

    @computed
    get linkDebugInfo(): Record<string, string> {
        return getDebugInfo(this.linkErrorObject, this.baseDebugInfo);
    }

    @action
    setIsLoading = (isLoading: boolean): void => {
        this.isLoading = isLoading;
    }

    @action
    setIsContentLoading = (isContentLoading: boolean): void => {
        this.isContentLoading = isContentLoading;
    }

    @action
    setErrorType = (errorType: EntityLoadErrorType): void => {
        this.errorType = errorType;
    }

    @action
    setLinkData = (linkData: LinkDetails): void => {
        this.linkData = linkData;
    }

    @action
    setIsMFARequired = (isMFARequired: boolean): void => {
        this.isMFARequired = isMFARequired;
    }

    @action
    setLinkId = (linkId: string): void => {
        this.linkId = linkId;
    }

    @action
    setFilesList = (files: SharedFile[]): void => {
        this.filesList = files;
    }

    @action
    setSelectedFile = (fileId: string): void => {
        this.selectedFileId = fileId;
        this.selectedFileIndex = this.filesList.indexOf(this.selectedFile);
    }

    @action
    setFilePermission = (fileId: string, permission: PermissionsState): void => {
        this.filesPermissions.set(fileId, permission);
    }

    @action
    setLinkErrorObject = (value: unknown): void => {
        this.linkErrorObject = value;
    }

    @action
    updateRequestsMap = (name: string, value: boolean): void => {
        this.allRequestsMap.set(name, value);
    }

    @action
    updatePermissionsLoaded = (value: boolean): void => {
        this.permissionsLoaded = value;
    }

    @action
    setPasswordHint = (hint: string | null): void => {
        this.passwordHint = hint;
    }

    tryFetchLink = async (silent = false): Promise<void> => {
        if (!silent) {
            this.setIsLoading(true);
            this.updateRequestsMap('tryFetchLink', true);
        }
        try {
            await this.fetchLinkData();
            await this.fetchFiles();
            // TODO: provide way to run it in parallel with fetchFiles()
            if (this.userStore.isUserSetUp) {
                await this.tryBatchAddPermissions({ isSilent: true });
            }
            this.setErrorType(null);
            this.setLinkErrorObject(undefined);
        } catch (error) {
            this.setLinkErrorObject(error);
            const errorType = errorToErrorType(error);
            captureLinkLoadError(errorType, error);
            this.setErrorType(errorType);
        }
        if (!silent) {
            this.setIsLoading(false);
            this.updateRequestsMap('tryFetchLink', false);
        }
    }

    tryBatchAddPermissions = async ({
        isSilent = false,
        targetFilesIds = [],
    }: AddPermissionsArgs = ADD_PERMISSIONS_DEFAULT_ARGS): Promise<void> => {
        const {
            linkId,
            authSettingsStore: { API },
            userStore: { currentUserEmail },
        } = this;
        if (!isSilent) {
            this.setIsLoading(true);
        }
        const filesIds: string[] = targetFilesIds?.length
            ? targetFilesIds
            : this.itemsIds;

        runInAction(() => {
            filesIds.forEach((fileId) => {
                this.filesPermissions.set(fileId, { summary: 'loading' });
            });
        });

        const filesWithNewPolicy: string[] = [];

        await Promise.all<void>(filesIds.map(async (fileId) => {
            await this.permissionsCheckQueue.processSimpleRequest(
                { id: fileId },
                async () => {
                    const permissionsState = await tryAddPermissionsToFile(API, {
                        fileId,
                        email: currentUserEmail,
                        linkId,
                    });
                    this.setFilePermission(fileId, permissionsState);
                    if (permissionsState.policyId) {
                        filesWithNewPolicy.push(fileId);
                    }
                    return true;
                },
            );
        }));

        if (filesWithNewPolicy.length) {
            await this.refreshFilesPolicies(API, filesWithNewPolicy);
        }

        if (!isSilent) {
            this.setIsLoading(false);
        }
        this.updatePermissionsLoaded(true);
    }

    async tryRefreshPermissions(fileId: string): Promise<void> {
        const file = this.filesList.find(({ file_id: id }) => id === fileId);
        const { currentUserIdentity } = this.userStore;
        const { API } = this.authSettingsStore;
        if (file && !eqUserIdentities(currentUserIdentity, { id: file.owner, email: file.owner_email })) {
            try {
                // TODO: update options based on this refresh
                const refreshedFile = await getFile(API, fileId);
                this.updateFilePermissions(fileId, { editAllowed: refreshedFile.menu?.read_only === false });
            } catch (error) {
                console.log('could not fetch file permissions', error);
                captureErrorForSentry(error, 'SharedFilesStore.tryRefreshPermissions');
            }
        }
    }

    private async refreshFilesPolicies(API: typeof this.authSettingsStore.API, filesIds: string[]): Promise<void> {
        try {
            const files = await fetchFilesChunked(API, filesIds);
            runInAction(() => {
                files.forEach((fetchedFile) => {
                    const existingFile = this.filesList.find((f) => f.file_id === fetchedFile.file_id);
                    if (existingFile) {
                        existingFile.policy = fetchedFile.policy;
                    }
                });
            });
        } catch (error) {
            captureErrorForSentry(error, 'SharedFilesStore.refreshFilesPolicies');
        }
    }

    private async fetchLinkData(): Promise<void> {
        const { API } = this.authSettingsStore;
        const linkDetails = await fetchExistingLink(API, this.linkId);
        if (linkDetails?.organization_id) {
            localStorage.setItem(CURRENT_ORGANIZATION, linkDetails.organization_id);
        }
        this.setLinkData(linkDetails);
    }

    private async fetchFiles(): Promise<void> {
        const { API } = this.authSettingsStore;
        let filesIds: string[] = this.linkData.files_ids;
        if (!filesIds?.length) {
            filesIds = [this.linkData.file_id];
        }
        if ((filesIds?.length && !filesIds[0]) && this.linkData.message_id) {
            filesIds = [this.linkData.message_id];
        }
        const files = await fetchFilesChunked(API, filesIds);
        this.setFilesList(files);
    }

    private updateFilePermissions(fileId: string, statePartial: Partial<PermissionsState>): void {
        const permissions = this.filesPermissions.get(fileId);
        if (permissions) {
            this.setFilePermission(fileId, { ...permissions, ...statePartial });
        }
    }
}

export default SharedFilesStore;
